import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { array, func, object, string } from 'prop-types';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { ResponsiveImage, NamedLink, Button } from '../../components';
import { formatMoney, convertPrice } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { HOURLY_PRICE_PACKAGES, calculateSubTotal } from '../../containers/CartPage/CartHelpers';
import { createSlug } from '../../util/urlHelpers';
import { generateCategorySlug, generateCitySlug } from '../../util/seo';
import {
    isMiniCartDismissed,
    setMiniCartDismissed,
    getAbandonedCheckout,
    saveAbandonedCheckout,
    clearAbandonedCheckout,
    clearMiniCartDismissed,
    addItemToSessionStorageCart,
    getCartFromSession,
} from '../../util/sessionStorageManager';
import { storedData } from '../../containers/CheckoutPage/CheckoutPageSessionHelpers';
import { fetchTransactionLineItems } from '../../containers/CartPage/CartPage.duck';
import { startCheckoutFromAbandonedData } from '../../containers/CheckoutPage/checkoutFromCart';
import { applyListingLanguage } from '../../util/listingLanguage';
import { fetchExchangeRates } from '../../ducks/exchangeRates.duck';
import { matchPathname } from '../../util/routes';
import { useConfiguration } from '../../context/configurationContext';
import { addToCart } from '../../ducks/cart.duck';

import css from './FloatingCheckoutWidget.module.css';
import { useSelector } from 'react-redux';

const { Money } = sdkTypes;
const CHECKOUT_STORAGE_KEY = 'CheckoutPage';
const FETCH_METADATA_KEY = 'FloatingCheckoutWidget.fetchMetadata';
const TEN_MINUTES_MS = 10 * 60 * 1000;

// Routes where widget should be hidden
const HIDDEN_ROUTES = new Set([
    'ListingPage',
    'ListingPageCity',
    'ListingPageCityCategory',
    'ListingPageCategory',
    'ListingPageVariant',
    'ListingPageVariantOld',
    'CheckoutPage',
    'CheckoutPageOld',
    'ProfilePage',
    'ProfilePageAbout',
    'ProfilePageReviews',
    'ProfilePageGallery',
    'ProfilePageFaqs',
    'ProfilePageBlog',
    'ProfilePageNoCity',
    'ProfilePageNoCityAbout',
    'ProfilePageNoCityReviews',
    'ProfilePageNoCityGallery',
    'ProfilePageNoCityFaqs',
    'ProfilePageNoCityBlog',
    'ProfilePageLegacy',
    'CartPage',
    'SignupPage',
    'LoginPage',
    'ConfirmPage'
]);

/**
 * Creates a unique signature for a checkout based on listing IDs, dates, package, and quantity
 * Used to detect if a new checkout has been started
 * Includes: listingId|startDate|endDate|package|quantity
 */
const getCheckoutSignature = (checkout) => {
    if (!checkout?.orderData) return '';
    const orderDataArray = Array.isArray(checkout.orderData) ? checkout.orderData : [checkout.orderData];

    const signatures = orderDataArray
        .map(item => {
            const listingId = item.listing?.id?.uuid || checkout.listing?.id?.uuid;
            if (!listingId) return null;

            const bookingDates = item.bookingDates || {};
            const startDate = bookingDates.bookingStart || bookingDates.startDate;
            const endDate = bookingDates.bookingEnd || bookingDates.endDate;
            const packageName = item.package || '';
            const quantity = item.seats || item.quantity || 1;

            // Create unique signature: listingId|startDate|endDate|package|quantity
            // Format dates consistently (YYYY-MM-DD) for comparison
            let dateStr = '';
            if (startDate && endDate) {
                try {
                    const start = moment(startDate).format('YYYY-MM-DD');
                    const end = moment(endDate).format('YYYY-MM-DD');
                    dateStr = `${start}|${end}`;
                } catch (e) {
                    // Fallback to ISO string if moment fails
                    dateStr = `${startDate}|${endDate}`;
                }
            }

            return `${listingId}|${dateStr}|${packageName}|${quantity}`;
        })
        .filter(Boolean)
        .sort();

    // Join multiple items with double pipe to distinguish from single item separators
    return signatures.join('||') || checkout.listing?.id?.uuid || '';
};

/**
 * Extracts the correct package from line items code
 * Line items have codes like "line-item/item-1: Title:-twoDays"
 */
const extractPackageFromLineItems = (lineItems, fallbackPackage) => {
    if (!lineItems?.length) return fallbackPackage;

    const itemLineItem = lineItems.find(item => item.code?.includes('line-item/item'));
    if (!itemLineItem) return fallbackPackage;

    const match = itemLineItem.code.match(/:-(\w+)$/);
    if (!match) return fallbackPackage;

    const extracted = match[1];
    return extracted.endsWith('Price') ? extracted : `${extracted}Price`;
};

/**
 * Get/set fetch metadata from sessionStorage
 */
const getFetchMetadata = () => {
    try {
        const data = sessionStorage.getItem(FETCH_METADATA_KEY);
        return data ? JSON.parse(data) : { signature: null, timestamp: null };
    } catch (e) {
        return { signature: null, timestamp: null };
    }
};

const setFetchMetadata = (signature, timestamp) => {
    try {
        sessionStorage.setItem(FETCH_METADATA_KEY, JSON.stringify({ signature, timestamp }));
    } catch (e) {
        // Silently fail - metadata caching is non-critical
    }
};

/**
 * Helper function to add abandoned checkout items to cart (background backup)
 * Only adds items that don't already exist in cart to avoid duplicates
 */
const addAbandonedCheckoutToCart = async (abandonedData, cartItems, isAddingToCartInProgress, currentUser, dispatch, config) => {
    if (!abandonedData?.orderData || !abandonedData?.listing) return;

    try {
        const { orderData, listing } = abandonedData;
        const orderDataArray = Array.isArray(orderData) ? orderData : [orderData];
        const authorId = listing?.author?.id?.uuid;
        const timeZone = listing?.attributes?.availabilityPlan?.timezone || 'Etc/UTC';
        const isAuthenticated = !!currentUser?.id;

        // Get existing cart to check for duplicates
        let existingCart = [];
        if (isAuthenticated && !isAddingToCartInProgress) {
            existingCart = Object.values(cartItems || {}).flat();
        } else if (!isAddingToCartInProgress) {
            const sessionCart = getCartFromSession();
            existingCart = sessionCart || [];
        }

        // Process each item in orderData
        for (const item of orderDataArray) {
            const itemListing = item.listing || listing;
            const listingId = itemListing?.id?.uuid;
            const selectedPackage = item.package;
            const seats = item.seats || 1;
            const bookingDates = item.bookingDates || {};

            // Convert booking dates format
            const bookingStart = bookingDates.bookingStart || bookingDates.startDate;
            const bookingEnd = bookingDates.bookingEnd || bookingDates.endDate;

            if (!listingId || !selectedPackage || !bookingStart || !bookingEnd) {
                continue; // Skip invalid items
            }

            // Check if item already exists in cart (for guest users)
            const exists = existingCart.some(cartItem => {
                const cartStart = cartItem.bookingDates?.startDate;
                const cartEnd = cartItem.bookingDates?.endDate;
                return (
                    cartItem.listingId === listingId &&
                    cartItem.package === selectedPackage &&
                    moment(cartStart).isSame(bookingStart) &&
                    moment(cartEnd).isSame(bookingEnd)
                );
            });

            if (exists) {
                console.log('item already in cart, skipping...');
                continue; // Skip if already in cart
            }

            // Prepare cart params
            // Note: Sharetribe API requires ISO string format for dates, not Date objects
            const cartParams = {
                authorId,
                listingId,
                selectedPackage,
                bookingDates: {
                    startDate: moment(bookingStart).tz(timeZone).toISOString(),
                    endDate: moment(bookingEnd).tz(timeZone).toISOString(),
                },
                timeZone,
                quantity: seats,
                units: 1,
                usageMultipleValues: item.usageMultipleValues,
                selectedFeeTypes: item.selectedFeeTypes || item.otherOptionalFees || [],
                pickupAtOwnerLocation: item.pickupAtOwnerLocation,
                returnAtOwnerLocation: item.returnAtOwnerLocation,
            };

            if (isAuthenticated) {
                // For authenticated users, dispatch addToCart (handles deduplication internally)
                if (config) {
                    dispatch(addToCart(config, cartParams, null)).catch(() => {
                        // Silently fail - cart addition is backup only
                    });
                }
            } else {
                // For guest users, use sessionStorage directly
                try {
                    addItemToSessionStorageCart(cartParams);
                } catch (e) {
                    // Silently fail - cart addition is backup only
                }
            }
        }
    } catch (error) {
        // Silently fail - cart addition is backup only, shouldn't block widget
        console.warn('Failed to add abandoned checkout to cart:', error);
    }
};

/**
 * Floating widget that displays abandoned checkout items
 * Shows on all pages except listing, checkout, and cart pages
 */
const FloatingCheckoutWidget = ({
    intl,
    currentUserCurrency,
    exchangeRates,
    currentUser,
    dispatch,
    history,
    routeConfiguration,
    lineItems,
    fetchLineItemsInProgress,
}) => {
    const { pathname } = useLocation();
    const config = useConfiguration();

    // Component state
    const [isExpanded, setIsExpanded] = useState(true);
    const [abandonedCheckout, setAbandonedCheckout] = useState(getAbandonedCheckout);
    const [fetchedForListingId, setFetchedForListingId] = useState(null);
    const [fetchingForListingId, setFetchingForListingId] = useState(null);
    const [position, setPosition] = useState({ top: null, right: null, bottom: null, arrowLeft: null });
    const { items: cartItems, setItemsInProgress: isAddingToCartInProgress } = useSelector(state => state.cart);
    const widgetRef = useRef(null);
    const cartRestoreAttemptedForRef = useRef(null); // tracks sessionStoredAt of last cart restore attempt

    // Page type detection using route matching (works across all languages)
    const currentRouteName = useMemo(() => {
        const matchedRoutes = matchPathname(pathname, routeConfiguration);
        return matchedRoutes.length > 0 ? matchedRoutes[0].route.name : null;
    }, [pathname, routeConfiguration]);

    const shouldHideWidget = useMemo(() => {
        return currentRouteName ? HIDDEN_ROUTES.has(currentRouteName) : false;
    }, [currentRouteName]);

    // Derived values from abandoned checkout
    const abandonedAuthorId = abandonedCheckout?.authorId;
    const currentListingId = abandonedCheckout?.listing?.id?.uuid;
    const lineItemsFromRedux = abandonedAuthorId ? lineItems?.[abandonedAuthorId] : null;
    const isFetchingLineItems = abandonedAuthorId ? fetchLineItemsInProgress?.[abandonedAuthorId] : false;

    // Get checkout signature for dismissal tracking
    const checkoutSignature = useMemo(() => {
        return abandonedCheckout ? getCheckoutSignature(abandonedCheckout) : null;
    }, [abandonedCheckout]);

    // Initialize dismissal state based on current checkout signature
    const [isDismissed, setIsDismissed] = useState(() => {
        const initialCheckout = getAbandonedCheckout();
        const initialSignature = initialCheckout ? getCheckoutSignature(initialCheckout) : null;
        return initialSignature ? isMiniCartDismissed(initialSignature) : false;
    });

    // Re-check dismissal state on route changes or when checkout changes
    useEffect(() => {
        if (checkoutSignature) {
            setIsDismissed(isMiniCartDismissed(checkoutSignature));
        } else {
            setIsDismissed(false);
        }
    }, [pathname, checkoutSignature]);

    // Fetch exchange rates if not already loaded (for currency conversion)
    useEffect(() => {
        const hasExchangeRates = exchangeRates && exchangeRates.length > 0;
        if (!hasExchangeRates && abandonedCheckout) {
            dispatch(fetchExchangeRates());
        }
    }, [exchangeRates, abandonedCheckout, dispatch]);

    // Detect abandoned checkout on route changes
    useEffect(() => {
        const isCheckoutPage = currentRouteName === 'CheckoutPage' || currentRouteName === 'CheckoutPageOld';
        if (isCheckoutPage) return;

        const checkoutData = storedData(CHECKOUT_STORAGE_KEY);
        const existingAbandonedCheckout = getAbandonedCheckout();

        if (checkoutData?.orderData && checkoutData?.listing) {
            // If the checkout listing belongs to the current user, this was a quote-preparation
            // session. Owners can never book their own listings — skip the abandoned-checkout
            // restore and widget entirely. This check is stateless and user-scoped, so it can
            // never incorrectly suppress the widget for a different logged-in user.
            const listingAuthorId = checkoutData.listing?.author?.id?.uuid;
            const isOwnListingSession =
                listingAuthorId && currentUser?.id?.uuid && listingAuthorId === currentUser.id.uuid;
            if (isOwnListingSession) return;

            const sessionStoredAt = checkoutData.storedAt ? new Date(checkoutData.storedAt).getTime() : Date.now();
            const localStorageAbandonedAt = existingAbandonedCheckout?.abandonedAt
                ? new Date(existingAbandonedCheckout.abandonedAt).getTime()
                : 0;

            if (sessionStoredAt > localStorageAbandonedAt) {
                const abandonedData = {
                    listing: checkoutData.listing,
                    orderData: checkoutData.orderData,
                    lineItems: checkoutData.lineItems,
                    withinNinetyDays: checkoutData.withinNinetyDays,
                    returnCareInstructions: checkoutData.returnCareInstructions,
                    authorId: checkoutData.listing?.author?.id?.uuid,
                };

                saveAbandonedCheckout(abandonedData);
                setAbandonedCheckout(abandonedData);

                // Restore cart items once per checkout session (keyed by storedAt timestamp).
                // The ref prevents re-running when cartItems changes (e.g. user removes an item),
                // while still firing for same-signature checkouts that were previously dismissed.
                if (cartRestoreAttemptedForRef.current !== sessionStoredAt) {
                    cartRestoreAttemptedForRef.current = sessionStoredAt;
                    addAbandonedCheckoutToCart(abandonedData, cartItems, isAddingToCartInProgress, currentUser, dispatch, config);
                }

                // Only clear dismissal if this is a genuinely NEW checkout
                const newSignature = getCheckoutSignature(abandonedData);
                const oldSignature = getCheckoutSignature(existingAbandonedCheckout);

                if (newSignature !== oldSignature) {
                    // New checkout detected - clear dismissal and show widget
                    clearMiniCartDismissed();
                    setIsDismissed(false);
                    setFetchMetadata(null, null); // Clear cache for new checkout
                } else {
                    // Same checkout - check if it was dismissed
                    setIsDismissed(isMiniCartDismissed(newSignature));
                }
            } else if (existingAbandonedCheckout) {
                setAbandonedCheckout(existingAbandonedCheckout);
                // Check dismissal state for existing checkout
                const existingSignature = getCheckoutSignature(existingAbandonedCheckout);
                if (existingSignature) {
                    setIsDismissed(isMiniCartDismissed(existingSignature));
                }
            }
        } else if (existingAbandonedCheckout) {
            setAbandonedCheckout(existingAbandonedCheckout);
            // Check dismissal state for existing checkout
            const existingSignature = getCheckoutSignature(existingAbandonedCheckout);
            if (existingSignature) {
                setIsDismissed(isMiniCartDismissed(existingSignature));
            }
        }
    }, [currentRouteName, pathname, currentUser, dispatch, config, cartItems, isAddingToCartInProgress]);

    // Reset fetch tracking when listing changes
    useEffect(() => {
        if (currentListingId && fetchedForListingId && currentListingId !== fetchedForListingId) {
            setFetchedForListingId(null);
            setFetchingForListingId(null);
            setFetchMetadata(null, null); // Clear metadata to allow new fetch
        }
    }, [currentListingId, fetchedForListingId]);

    // Mark as fetched when fetch completes
    useEffect(() => {
        if (!isFetchingLineItems && fetchingForListingId && lineItemsFromRedux) {
            setFetchedForListingId(fetchingForListingId);
            setFetchingForListingId(null);
        }
    }, [isFetchingLineItems, fetchingForListingId, lineItemsFromRedux]);

    // Fetch line items for abandoned checkout
    // Optimized: Uses checkout signature to prevent redundant API calls on route/language changes
    useEffect(() => {
        if (!abandonedCheckout || !abandonedAuthorId || isFetchingLineItems) return;

        const { orderData, listing } = abandonedCheckout;
        if (!orderData || !listing) return;

        // Create stable signature for this checkout (based on listing IDs)
        const checkoutSignature = getCheckoutSignature(abandonedCheckout);

        // Get fetch metadata from sessionStorage (persists across navigation)
        const fetchMetadata = getFetchMetadata();
        const storedSignature = fetchMetadata.signature;
        const storedTimestamp = fetchMetadata.timestamp;

        // Check if already fetched AND data exists in Redux (handles page reload scenarios)
        const signatureMatches = storedSignature === checkoutSignature;
        const hasData = !!lineItemsFromRedux && lineItemsFromRedux.length > 0;

        // Check if data is fresh (fetched within last 10 minutes)
        const now = Date.now();
        const timeSinceFetch = storedTimestamp ? now - storedTimestamp : null;
        const isFresh = storedTimestamp && timeSinceFetch < TEN_MINUTES_MS;

        const alreadyFetched = signatureMatches && hasData && isFresh;

        if (alreadyFetched) {
            // Data exists and is fresh, just ensure fetchedForListingId is set
            if (fetchedForListingId !== currentListingId) {
                setFetchedForListingId(currentListingId);
            }
            return;
        }

        // Skip if currently fetching
        if (fetchingForListingId === currentListingId) {
            return;
        }

        const orderDataArray = Array.isArray(orderData) ? orderData : [orderData];

        const fetchLineItemsData = orderDataArray.map((data) => {
            const itemListing = data.listing || listing;
            const bookingDates = data.bookingDates || {};

            return {
                orderData: {
                    bookingStart: bookingDates.bookingStart,
                    bookingEnd: bookingDates.bookingEnd,
                    seats: data.seats || 1,
                    package: extractPackageFromLineItems(data.lineItems, data.package),
                    usageMultipleValues: data.usageMultipleValues,
                    selectedFeeTypes: data.selectedFeeTypes || data.otherOptionalFees || [],
                    pickupAtOwnerLocation: data.pickupAtOwnerLocation,
                    returnAtOwnerLocation: data.returnAtOwnerLocation,
                },
                listingId: itemListing?.id?.uuid,
                isOwnListing: false,
            };
        });

        const fetchTimestamp = Date.now();
        setFetchingForListingId(currentListingId);
        setFetchMetadata(checkoutSignature, fetchTimestamp); // Store in sessionStorage
        dispatch(fetchTransactionLineItems(abandonedAuthorId, { fetchLineItemsData }));
    }, [abandonedCheckout, abandonedAuthorId, currentListingId, fetchingForListingId, isFetchingLineItems, dispatch]);

    // Transform orderData to display items with translations
    const abandonedCheckoutItems = useMemo(() => {
        if (!abandonedCheckout?.orderData) return null;

        const { orderData, listing } = abandonedCheckout;
        const orderDataArray = Array.isArray(orderData) ? orderData : [orderData];
        const locale = intl.locale;

        return orderDataArray.map((data, index) => {
            const itemListing = data.listing || listing;
            // Apply language translation to listing
            const translatedListing = applyListingLanguage(itemListing, locale);

            return {
                id: data.id || `abandoned-${index}`,
                listing: translatedListing,
                bookingDates: data.bookingDates,
                timeZone: translatedListing?.attributes?.availabilityPlan?.timezone || 'Etc/UTC',
                package: data.package,
                quantity: data.seats || 1,
            };
        });
    }, [abandonedCheckout, intl.locale]);

    // Line items only valid if fetch completed for current listing
    const abandonedCheckoutLineItems = (fetchedForListingId === currentListingId) ? lineItemsFromRedux : null;

    // Get item line items (rental items only, not fees)
    const itemLineItems = useMemo(() => {
        if (!abandonedCheckoutLineItems?.length) return [];
        return abandonedCheckoutLineItems.filter(
            ({ includeFor, item }) => item || (includeFor?.[0] === 'customer' && includeFor?.[1] === 'provider')
        );
    }, [abandonedCheckoutLineItems]);

    // Visibility logic - only show when we have items, prices, AND exchange rates
    // Widget waits for exchange rates to load for proper currency conversion
    const hasData = abandonedCheckoutItems?.length > 0;
    const hasPriceData = abandonedCheckoutLineItems?.length > 0;
    const hasExchangeRatesLoaded = exchangeRates && exchangeRates.length > 0;
    const isVisible = hasData && hasPriceData && hasExchangeRatesLoaded && !isDismissed && !shouldHideWidget;
    const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;
    // Calculate position relative to cart link
    useEffect(() => {
        if (!isVisible) return;

        const updatePosition = () => {
            const isMobile = window.innerWidth <= 768;
            const cartLink = document.querySelector('[data-cart-link="true"]');

            if (!cartLink) {
                // Fallback to default position if cart link not found
                // Still set arrow to center (50%) for visibility
                setPosition({ top: null, right: null, bottom: null, arrowLeft: 50 });
                return;
            }

            // On mobile, get the parent container (iconWrapper or navItem) for accurate positioning
            // since data-cart-link is just an empty span
            const cartElement = isMobile
                ? (cartLink.closest('[class*="iconWrapper"]') || cartLink.closest('[class*="navItem"]') || cartLink.parentElement)
                : cartLink;

            const cartRect = cartElement.getBoundingClientRect();
            const gap = 8;

            if (isMobile) {
                // Mobile positioning: widget is full-width (minus 16px margins) and centered in viewport.
                // Arrow should be perfectly centered in the widget, not relative to cart button.
                const widgetWidth = window.innerWidth - 32; // Full width minus 16px margins on both sides

                // Find the mobile navbar element to get its actual height
                const mobileNavbar = document.querySelector('[class*="MobileBottomNavbar"]') ||
                    document.querySelector('nav[class*="navbar"]');

                let bottom;
                if (mobileNavbar) {
                    const navbarRect = mobileNavbar.getBoundingClientRect();
                    const navbarHeight = navbarRect.height;
                    // Position widget just above navbar with small gap
                    bottom = navbarHeight + gap;
                } else {
                    // Fallback: assume ~72px navbar height
                    bottom = 72 + gap;
                }

                // Fixed horizontal position: 16px margin from both sides
                const finalRight = 16;

                // Arrow centered in widget on mobile
                const arrowLeftPercent = 50;

                setPosition({ top: null, right: finalRight, bottom: bottom, arrowLeft: arrowLeftPercent });
            } else {
                // Desktop positioning: position below the cart link
                const widgetWidth = 400; // Widget width from CSS

                // Position widget below the cart link
                const top = cartRect.bottom + gap;

                // Calculate center of cart button
                const cartButtonCenter = cartRect.left + (cartRect.width / 2);

                // Center widget relative to cart button
                const widgetCenter = cartButtonCenter;
                const widgetLeft = widgetCenter - (widgetWidth / 2);

                // Calculate right position: distance from right edge of viewport
                const right = window.innerWidth - (widgetLeft + widgetWidth);

                // Ensure widget doesn't go off-screen
                const minRight = 24; // Minimum margin from right edge
                const maxRight = window.innerWidth - widgetWidth - 24; // Maximum right position

                // Ensure widget doesn't go off-screen on the top
                const minTop = 55; // Minimum margin from top

                const finalRight = Math.max(minRight, Math.min(right, maxRight));
                const finalTop = Math.max(minTop, top);

                // Recalculate widgetLeft after constraints are applied
                const finalWidgetLeft = window.innerWidth - (finalRight + widgetWidth);

                // Calculate arrow position to center it relative to cart button
                const cartCenterRelativeToWidget = cartButtonCenter - finalWidgetLeft;
                const arrowLeftPercent = (cartCenterRelativeToWidget / widgetWidth) * 100;

                // Clamp arrow position to stay within widget bounds (with some margin)
                const clampedArrowPercent = Math.max(10, Math.min(90, arrowLeftPercent));

                setPosition({ top: finalTop, right: finalRight, bottom: null, arrowLeft: clampedArrowPercent });
            }
        };

        // Initial calculation with a small delay to ensure DOM is ready
        const timeoutId = setTimeout(updatePosition, 100);

        // Update on scroll and resize
        const handleScroll = () => requestAnimationFrame(updatePosition);
        const handleResize = () => requestAnimationFrame(updatePosition);

        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);

        // Use ResizeObserver to watch for cart link position changes
        const cartLink = document.querySelector('[data-cart-link="true"]');
        let resizeObserver = null;

        if (cartLink && window.ResizeObserver) {
            resizeObserver = new ResizeObserver(() => {
                requestAnimationFrame(updatePosition);
            });
            resizeObserver.observe(cartLink);
        }

        // Also observe the topbar container for layout changes
        const topbar = document.querySelector('nav[class*="TopbarDesktop"]');
        if (topbar && window.ResizeObserver) {
            if (!resizeObserver) {
                resizeObserver = new ResizeObserver(() => {
                    requestAnimationFrame(updatePosition);
                });
            }
            resizeObserver.observe(topbar);
        }

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, [isVisible, pathname]);


    useEffect(() => {
        if (isMobileView) {
            setIsExpanded(false)
        }
    }, [isMobileView]);

    // Calculate item count and subtotal
    const { itemCount, formattedTotal } = useMemo(() => {
        if (!abandonedCheckoutItems?.length) {
            return { itemCount: 0, formattedTotal: '$0' };
        }

        const count = abandonedCheckoutItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

        const total = abandonedCheckoutLineItems?.length
            ? calculateSubTotal(abandonedCheckoutLineItems, currentUserCurrency, intl, exchangeRates) || '$0'
            : '$0';

        return { itemCount: count, formattedTotal: total };
    }, [abandonedCheckoutItems, abandonedCheckoutLineItems, currentUserCurrency, intl, exchangeRates]);

    // Format individual item price with currency conversion
    const formatItemPrice = useCallback((lineItem) => {
        if (!lineItem?.lineTotal) return null;
        const { amount, currency } = lineItem.lineTotal;
        const convertedPrice = convertPrice(exchangeRates, amount, currency, currentUserCurrency);
        if (!convertedPrice) return null;
        return formatMoney(intl, new Money(convertedPrice.amount, currentUserCurrency));
    }, [exchangeRates, currentUserCurrency, intl]);

    // Event handlers
    const handleCheckout = useCallback(async () => {
        if (!abandonedCheckout || !abandonedCheckoutItems?.length) return;

        const signatureToDismiss = checkoutSignature;

        try {
            await startCheckoutFromAbandonedData({
                abandonedCheckout,
                cartItems: Object.values(cartItems || {}).flat(),
                isAddingToCartInProgress,
                currentUser,
                routeConfiguration,
                dispatch,
                history,
            });
        } catch (error) {
            console.error('Error starting checkout from abandoned checkout:', error);
        } finally {
            clearAbandonedCheckout();
            setAbandonedCheckout(null);
            // Mark as dismissed so the popup does not reappear when user returns to home page
            if (signatureToDismiss) {
                setMiniCartDismissed(signatureToDismiss);
                setIsDismissed(true);
            }
        }
    }, [abandonedCheckout, abandonedCheckoutItems, checkoutSignature, cartItems, isAddingToCartInProgress, currentUser, routeConfiguration, dispatch, history]);

    const handleDismiss = useCallback((e) => {
        e.stopPropagation();
        if (checkoutSignature) {
            setMiniCartDismissed(checkoutSignature);
            setIsDismissed(true);
        }
    }, [checkoutSignature]);

    // Early return after all hooks
    if (!isVisible) return null;

    // Render individual item
    const renderItem = (item, index) => {
        const { id, listing, bookingDates, timeZone, package: selectedPackage } = item;
        const { title } = listing?.attributes || {};
        const image = listing?.images?.[0];

        // Handle both {startDate, endDate} and {bookingStart, bookingEnd} formats
        const startDate = bookingDates?.startDate || bookingDates?.bookingStart;
        const endDate = bookingDates?.endDate || bookingDates?.bookingEnd;
        const isHourly = HOURLY_PRICE_PACKAGES.includes(selectedPackage);

        // Get corresponding line item price
        const lineItem = itemLineItems[index];
        const itemPrice = formatItemPrice(lineItem);

        // Format dates for display
        const formatDate = (date, subtractDay = false) => {
            if (!date) return '';
            let m = timeZone ? moment(date).tz(timeZone) : moment(date);
            if (subtractDay) m = m.subtract(1, 'day');
            return m.format('MMMM Do YYYY');
        };

        const displayStartDate = formatDate(startDate);
        const displayEndDate = formatDate(endDate, timeZone && !isHourly);

        // Create listing link props if listing exists
        const listingId = listing?.id?.uuid;
        const listingSlug = title ? createSlug(title) : '';
        const listingCitySlug = generateCitySlug(listing?.attributes?.publicData?.city);
        const listingCategorySlug = generateCategorySlug(listing?.attributes?.publicData?.category);
        const listingLinkProps = listingId && listingSlug ? {
            name: 'ListingPage',
            params: {
                ...(listingCitySlug ? { city: listingCitySlug } : {}),
                ...(listingCategorySlug ? { category: listingCategorySlug } : {}),
                id: listingId,
                slug: listingSlug
            }
        } : null;

        const itemContent = (
            <div className={css.itemPreview}>
                {image && (
                    <div className={css.imageWrapper}>
                        <ResponsiveImage
                            rootClassName={css.image}
                            alt={title}
                            image={image}
                            variants={['scaled-small', 'scaled-medium']}
                        />
                    </div>
                )}
                <div className={css.itemDetails}>
                    <h4 className={css.itemTitle}>{title || 'Rental Item'}</h4>
                    {displayStartDate && displayEndDate && (
                        <p className={css.dates}>{displayStartDate} - {displayEndDate}</p>
                    )}
                    {itemPrice && <div className={css.itemPrice}>{itemPrice}</div>}
                </div>
            </div>
        );

        return listingLinkProps ? (
            <NamedLink key={id} {...listingLinkProps} className={css.itemLink}>
                {itemContent}
            </NamedLink>
        ) : (
            <div key={id}>
                {itemContent}
            </div>
        );
    };

    // Calculate inline styles for positioning

    const positionStyles = (position.top !== null || position.bottom !== null) && position.right !== null
        ? {
            ...(position.top !== null && { top: `${position.top}px` }),
            ...(position.bottom !== null && { bottom: `${position.bottom}px` }),
            right: `${position.right}px`,
            left: isMobileView ? 'auto' : undefined,
            width: isMobileView ? `${window.innerWidth - 32}px` : undefined,
            ...(position.arrowLeft !== null && { '--arrow-left': `${position.arrowLeft}%` })
        }
        : {};

    return (
        <div
            ref={widgetRef}
            className={classNames(css.root, { [css.expanded]: isExpanded, [css.collapsed]: !isExpanded })}
            style={positionStyles}
        >
            <button
                className={css.closeButton}
                type="button"
                onClick={(e) => {
                    handleDismiss(e);
                }}
                aria-label={intl.formatMessage({ id: 'FloatingCheckoutWidget.closeMiniCart' })}
            >
                <i className='ri-close-line'></i>
            </button>

            <div
                className={css.header}
                onClick={isMobileView && !isExpanded ? handleCheckout : undefined}
                role={isMobileView && !isExpanded ? "button" : undefined}
                tabIndex={isMobileView && !isExpanded ? 0 : undefined}
                onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && isMobileView && !isExpanded) {
                        e.preventDefault();
                        handleCheckout();
                    }
                }}
            >
                <div>
                    <div className={css.headerContent}>
                        <h3 className={css.title}>
                            <FormattedMessage id="FloatingCheckoutWidget.title" values={{ count: itemCount }} />
                            <span className={css.itemBadge}>{itemCount} {itemCount === 1 ? 'Item' : 'Items'}</span>
                        </h3>
                        {/* <button className={css.toggleButton} type="button" aria-label="Toggle widget">
                        <svg className={css.toggleIcon} width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                                d={isExpanded ? 'M5 12.5L10 7.5L15 12.5' : 'M5 7.5L10 12.5L15 7.5'}
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button> */}
                    </div>
                    <p className={css.subtitle}>
                        <FormattedMessage id="FloatingCheckoutWidget.reservedNotice" />
                    </p>
                </div>
                <div className={css.checkoutButtonWrapper}>
                    <Button size="large" className={css.checkoutButton} onClick={handleCheckout} type="button">
                        <i className='ri-arrow-right-line'></i>
                    </Button>
                </div>
            </div>

            {isExpanded && (
                <div className={css.content}>
                    <div className={css.itemsList}>
                        {abandonedCheckoutItems.map((item, index) => renderItem(item, index))}
                    </div>
                    <div className={css.totalSectionContainer}>
                        <div className={css.totalSection}>
                            <span className={css.totalLabel}>Subtotal:</span>
                            <span className={css.totalAmount}>{formattedTotal}</span>
                        </div>

                        <Button size="large" className={css.checkoutButton} onClick={handleCheckout} type="button">
                            <FormattedMessage id="FloatingCheckoutWidget.completePayment" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

FloatingCheckoutWidget.defaultProps = {
    currentUserCurrency: null,
    exchangeRates: null,
    lineItems: null,
    fetchLineItemsInProgress: null,
};

FloatingCheckoutWidget.propTypes = {
    intl: intlShape.isRequired,
    currentUserCurrency: string,
    exchangeRates: array,
    currentUser: object,
    dispatch: func.isRequired,
    history: object.isRequired,
    routeConfiguration: array.isRequired,
    lineItems: object,
    fetchLineItemsInProgress: object,
};

export default FloatingCheckoutWidget;
